export default defineNuxtPlugin(() => {
  const { $unleash } = useNuxtApp()

  const { apisix } = useRuntimeConfig().public

  const route = useRoute()
  const redirectUrl = useCookie<string>('redirectUrl')

  let isRedirecting = false

  const allowsUnauthenticated = () => Boolean(route.meta.accessControl?.roles?.includes('unauthenticated'))

  const forceLogin = async () => {
    if (isRedirecting || allowsUnauthenticated() || route.name === 'logout') return
    isRedirecting = true
    redirectUrl.value = route.fullPath
    await navigateTo('/login')
  }

  let refreshInProgress: Promise<void> | null = null
  let initialTokenFetch: Promise<void> | null = null

  const directSignStore = useDirectSignStore()
  const authStore = useAuthStore()
  const userStore = useUserStore()

  // A revoked session needs the gateway logout: /login would get the same 403
  const forceLogout = () => {
    if (isRedirecting) return
    isRedirecting = true
    userStore.logoutUser()
  }

  const refreshLegacyToken = () => {
    refreshInProgress ??= authStore.refreshToken().finally(() => {
      refreshInProgress = null
    })
    return refreshInProgress
  }

  // The legacy Phoebe API
  const legacyApi = $fetch.create({
    baseURL: `${apisix.url}/phoebe`,
    credentials: 'include',
    async onRequest({ options }) {
      if (isRedirecting) {
        const abortController = new AbortController()
        options.signal = abortController.signal
        abortController.abort(new DOMException('Aborting because of impending redirect', 'AbortError'))
        return
      }

      // The token is in memory only, so fetch it once per page load instead of failing the first call with a 401
      if (!directSignStore.authToken && !authStore.accessToken && !allowsUnauthenticated()) {
        initialTokenFetch ??= refreshLegacyToken()
        await initialTokenFetch
      }

      if (directSignStore.authToken) {
        options.headers.set('Authorization', `Basic ${directSignStore.authToken}`)
      } else if (authStore.accessToken) {
        options.headers.set('Authorization', `Bearer ${authStore.accessToken}`)
      }

      const unleashSessionId = $unleash.client?.getContext().sessionId

      if (unleashSessionId) {
        options.headers.set('Unleash-Session-Id', unleashSessionId)
      }

      if (options.body && Object.getPrototypeOf(options.body) === Object.prototype) {
        options.headers.set('Content-Type', 'application/json')
      }
    },
    onResponse({ response }) {
      // FIXME: The backend does not provide correct content type headers, so we need to force-parse responses and cannot check for that header here
      if (response.ok && response._data) {
        if (typeof response._data === 'string') {
          try {
            response._data = JSON.parse(response._data)
          } catch {
            // Looks like the response is not JSON, so we don't need to do anything
          }
        }

        if (Object.getPrototypeOf(response._data) === Object.prototype || Array.isArray(response._data)) {
          response._data = objectToCamelCase(response._data)
        }
      }
    },
    async onResponseError({ response, options }) {
      const isRetry = typeof options.retry === 'number'

      if (response?.status && response.status >= 500 && route.name !== 'sorry') {
        // We want to give the server some time to recover and react locally if it doesn't
        if (response.status === 503 || (options.retry && options.retry > 0)) return
        const requestId = getRequestId(response)
        navigateTo(requestId ? { path: '/sorry', query: { requestId } } : '/sorry')
        markErrorHandled(response)
      } else if (isGatewayUnauthResponse(response)) {
        // If we get a 401 from the gateway, it means we don't have a (valid) session,
        // so we need to log in against the gateway
        await forceLogin()
        markErrorHandled(response)
      } else if (isGatewaySessionRevokedResponse(response)) {
        forceLogout()
        markErrorHandled(response)
      } else if (response?.status === 401) {
        // If we get a 401 from Phoebe, it might mean that our access token has expired and we need to refresh it
        if (isRetry) {
          // We already retried, so nothing more we can do
          // TODO: Logout
          return
        }

        // Retry with refreshed token
        await refreshLegacyToken()

        if (authStore.accessToken) {
          options.headers.set('Authorization', `Bearer ${authStore.accessToken}`)

          options.retryStatusCodes = [401]
          options.retry = 1
          options.retryDelay = 200
        } else {
          // Refresh failed, logout
          // TODO: Logout
        }
      }
    },
  })

  const api = $fetch.create({
    baseURL: `${apisix.url}/waverunner`,
    credentials: 'include',
    onRequest({ options }) {
      if (isRedirecting) {
        const abortController = new AbortController()
        options.signal = abortController.signal
        abortController.abort(new DOMException('Aborting because of impending redirect', 'AbortError'))
        return
      }

      const unleashSessionId = $unleash.client?.getContext().sessionId

      if (unleashSessionId) {
        options.headers.set('Unleash-Session-Id', unleashSessionId)
      }

      if (options.body && Object.getPrototypeOf(options.body) === Object.prototype) {
        options.headers.set('Content-Type', 'application/json')
      }
    },
    async onResponseError({ response, options }) {
      if (response?.status && response.status >= 500 && route.name !== 'sorry') {
        // We want to give the server some time to recover and react locally if it doesn't
        if (response.status === 503 || (options.retry && options.retry > 0)) return
        const requestId = getRequestId(response)
        await navigateTo(requestId ? { path: '/sorry', query: { requestId } } : '/sorry')
        markErrorHandled(response)
      } else if (isGatewayUnauthResponse(response)) {
        await forceLogin()
        markErrorHandled(response)
      } else if (isGatewaySessionRevokedResponse(response)) {
        forceLogout()
        markErrorHandled(response)
      }
    },
  })

  return {
    provide: {
      legacyApi,
      api,
    },
  }
})
